import { useState, useEffect } from 'react';

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

// Singleton global pour conserver l'invite d'installation entre les rendus
let deferredPrompt: BeforeInstallPromptEvent | null = null;
let isAppAlreadyInstalled = false;
let isInitialized = false;
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (err) {
      console.error('[Zeyilo PWA] Erreur listener:', err);
    }
  });
}

/**
 * Vérifie si l'application est actuellement ouverte et exécutée depuis la PWA installée (mode standalone / plein écran).
 * Dans ce cas, l'option d'installation ne doit pas être affichée.
 */
export function isPwaStandalone(): boolean {
  if (typeof window === 'undefined') return false;

  return Boolean(
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: fullscreen)').matches ||
    window.matchMedia?.('(display-mode: window-controls-overlay)').matches ||
    (window.navigator as any).standalone === true ||
    (document.referrer && document.referrer.includes('android-app://'))
  );
}

/**
 * Détecte si le navigateur prend en charge l'invite native d'installation PWA de Chrome/Chromium.
 * Exclut explicitement :
 * - Les environnements iOS (Safari, Chrome iOS, Firefox iOS) car le moteur WebKit bloque le prompt() natif JS.
 * - Mozilla Firefox (Desktop & Mobile) qui ne prend pas en charge l'invite native beforeinstallprompt.
 * - Apple Safari sur macOS.
 * Cible : Google Chrome, Microsoft Edge, Samsung Internet, Opera, Brave et les navigateurs basés sur Chromium.
 */
export function isChromePwaSupportedBrowser(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  // Si l'application tourne déjà en mode autonome PWA, pas besoin d'installer
  if (isPwaStandalone()) return false;

  const ua = navigator.userAgent || '';

  // 1. Exclure tout environnement iOS (iPhone, iPad, iPod)
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) return false;

  // 2. Exclure Firefox (Desktop & Mobile)
  if (/Firefox|FxiOS/i.test(ua)) return false;

  // 3. Exclure Safari macOS
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  if (isSafari) return false;

  // 4. Cibler les moteurs Chromium (Chrome, Edge, Samsung Internet, Brave, etc.)
  const isChromium =
    Boolean((window as any).chrome) ||
    /Chrome|Chromium|Edg|SamsungBrowser/i.test(ua);

  return isChromium;
}

/**
 * Initialise l'écouteur global pour capturer l'événement beforeinstallprompt de Chrome
 * et détecter l'installation effective de l'application.
 */
export function initPwaInstall(): void {
  if (typeof window === 'undefined' || isInitialized) return;
  isInitialized = true;

  // Si déjà en PWA standalone, aucun prompt n'est nécessaire
  if (isPwaStandalone()) return;

  // Récupérer le prompt capturé très tôt dans index.html si déjà présent
  if ((window as any).__zeyiloDeferredPrompt) {
    deferredPrompt = (window as any).__zeyiloDeferredPrompt;
  }

  // Écoute de l'événement beforeinstallprompt natif de Chrome
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // Empêche l'affichage automatique de l'infobar Chrome par défaut
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    (window as any).__zeyiloDeferredPrompt = deferredPrompt;
    notifyListeners();
  });

  // Écoute de l'événement appinstalled quand l'utilisateur termine l'installation
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    (window as any).__zeyiloDeferredPrompt = null;
    isAppAlreadyInstalled = true;
    notifyListeners();
    console.log('[Zeyilo PWA] Application installée avec succès sur le système.');
  });

  // Écoute de l'événement sur mesure si index.html a déclenché l'événement précoce
  window.addEventListener('zeyilo:pwa-prompt-ready', () => {
    if ((window as any).__zeyiloDeferredPrompt) {
      deferredPrompt = (window as any).__zeyiloDeferredPrompt;
      notifyListeners();
    }
  });

  // Détection complémentaire si l'app est déjà installée sur l'appareil (Chrome/Android)
  if (typeof navigator !== 'undefined' && 'getInstalledRelatedApps' in navigator) {
    (navigator as any).getInstalledRelatedApps().then((relatedApps: any[]) => {
      if (Array.isArray(relatedApps) && relatedApps.length > 0) {
        isAppAlreadyInstalled = true;
        deferredPrompt = null;
        notifyListeners();
      }
    }).catch(() => {});
  }
}

/**
 * Déclenche l'installation native PWA de Google Chrome
 */
export async function triggerChromePwaInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const promptEvent = deferredPrompt || (window as any).__zeyiloDeferredPrompt;

  if (!promptEvent || typeof promptEvent.prompt !== 'function') {
    console.warn('[Zeyilo PWA] Le prompt d\'installation Chrome n\'est pas disponible.');
    return 'unavailable';
  }

  try {
    // Déclenche l'invite d'installation native de Chrome
    await promptEvent.prompt();
    const choiceResult = await promptEvent.userChoice;
    console.log('[Zeyilo PWA] Résultat de l\'invite d\'installation :', choiceResult.outcome);

    if (choiceResult.outcome === 'accepted') {
      isAppAlreadyInstalled = true;
      deferredPrompt = null;
      (window as any).__zeyiloDeferredPrompt = null;
      notifyListeners();
    }

    return choiceResult.outcome;
  } catch (error) {
    console.error('[Zeyilo PWA] Erreur lors de l\'invite d\'installation :', error);
    return 'unavailable';
  }
}

/**
 * Hook React pour déterminer la disponibilité de l'installation et lancer l'invite native.
 */
export function usePwaInstall() {
  const checkCanInstall = () => {
    if (typeof window === 'undefined') return false;
    if (isPwaStandalone() || isAppAlreadyInstalled) return false;
    if (!isChromePwaSupportedBrowser()) return false;
    return Boolean(deferredPrompt || (window as any).__zeyiloDeferredPrompt);
  };

  const [canInstall, setCanInstall] = useState<boolean>(checkCanInstall);

  useEffect(() => {
    initPwaInstall();

    const handleChange = () => {
      setCanInstall(checkCanInstall());
    };

    handleChange();
    listeners.add(handleChange);
    return () => {
      listeners.delete(handleChange);
    };
  }, []);

  return {
    canInstall,
    isStandalone: isPwaStandalone(),
    isSupported: isChromePwaSupportedBrowser(),
    installPwa: triggerChromePwaInstall
  };
}
